/**
 * DEMO doctor roster — NOT REAL PEOPLE.
 *
 * The clinic has supplied no verified doctor roster. Every record here exists
 * only so the doctor templates have something to render and so replacing them
 * later is mechanical.
 *
 * Rules this file follows, deliberately:
 *
 * - No human names are invented anywhere. `name` is a bracketed role label in
 *   both languages, because a fabricated personal name reads as real even when
 *   it obviously is not — and this data may briefly be seen by the client or
 *   the public.
 * - `bio` is placeholder prose *about what belongs there*, not an invented
 *   biography.
 * - Every credential field (`education`, `certifications`, `memberships`,
 *   `yearsExperience`) is `Attributed` with `provenance: "demo"` and a
 *   `replaceWith` note naming exactly what the client must supply. No `value`
 *   contains a plausible-looking fake credential.
 * - `yearsExperience` is `0` as a deliberate non-value, never a flattering
 *   number.
 * - `photo` points at /public/placeholder.svg, never at a real clinic
 *   photograph of a real person.
 *
 * DEMO marker convention:
 *   English — `[DEMO]`
 *   Arabic  — `[بيانات تجريبية]`
 *
 * `provenance: "demo"` means `isSchemaEligible()` is false for all of these, so
 * none of it may be emitted into Person/Physician structured data.
 */

import type { Doctor, ImageRef, Localized } from "../types";

// ---------------------------------------------------------------------------
// Shared demo helpers
// ---------------------------------------------------------------------------

const EN_MARK = "[DEMO]";
const AR_MARK = "[بيانات تجريبية]";

/**
 * The shared placeholder portrait. Intentionally the generic /placeholder.svg
 * rather than any photograph in /public/images — using a real photo of a real
 * person would imply that person is the doctor described here.
 *
 * Dimensions are the file's real intrinsic size (see public/placeholder.svg).
 */
function demoPhoto(roleEn: string, roleAr: string): ImageRef {
  return {
    src: "/placeholder.svg",
    alt: {
      en: `${EN_MARK} Placeholder image. No photograph has been supplied for the ${roleEn} profile.`,
      ar: `${AR_MARK} صورة بديلة. لم تُقدَّم بعد أي صورة لملف ${roleAr}.`,
    },
    width: 1200,
    height: 1200,
    provenance: "demo",
  };
}

/** Placeholder bio prose, parameterised by specialty so it reads coherently. */
function demoBio(
  scopeEn: string,
  scopeAr: string,
): Localized<readonly string[]> {
  return {
    en: [
      `${EN_MARK} This is placeholder text, shown so the profile layout can be reviewed. The clinic will supply an approved biography covering the doctor's dental degree, the awarding institution, the year of graduation, and their registration number with the Egyptian Dental Syndicate.`,
      `The final biography should describe the clinical scope this doctor actually works in — for this profile, ${scopeEn} — the kinds of cases they handle week to week, and how they take a patient through diagnosis and a treatment plan before any treatment begins.`,
      `Nothing in these paragraphs is verified and none of it describes a real person. All of it is replaced in full once approved content arrives from the clinic.`,
    ],
    ar: [
      `${AR_MARK} هذا نص بديل مؤقت، الغرض منه مراجعة شكل صفحة الطبيب فقط. ستقدّم العيادة سيرة مهنية مُعتمدة تتضمّن الدرجة الجامعية في طب الأسنان، والجهة المانحة لها، وسنة التخرج، ورقم التسجيل في نقابة أطباء الأسنان.`,
      `من المتوقّع أن تصف السيرة النهائية مجال العمل السريري الفعلي لهذا الملف — وهو ${scopeAr} — وطبيعة الحالات التي يتعامل معها الطبيب، وأسلوبه في شرح التشخيص وخطة العلاج للمريض قبل البدء بأي إجراء.`,
      `لا تحتوي هذه الفقرات على أي معلومة مُتحقَّق منها، ولا تصف طبيبًا حقيقيًا، وتُستبدل بالكامل عند استلام المحتوى المُعتمد من العيادة.`,
    ],
  };
}

/** Languages: low-risk, but marked as awaiting confirmation rather than asserted. */
const DEMO_LANGUAGES: Localized<readonly string[]> = {
  en: ["Arabic (to be confirmed)", "English (to be confirmed)"],
  ar: ["العربية (بانتظار التأكيد)", "الإنجليزية (بانتظار التأكيد)"],
};

/** Replacement instructions, identical for every doctor. */
const REPLACE_EDUCATION =
  "Client to supply, per qualification: degree or programme title, awarding institution, city and country, and year awarded. Supply the Egyptian Dental Syndicate registration number alongside it. Nothing renders as verified until certificates are on file.";

const REPLACE_CERTIFICATIONS =
  "Client to supply, per certification: exact certification name, issuing body, year issued, and expiry or renewal date where one applies. A scan of each certificate is required before the item may be published.";

const REPLACE_MEMBERSHIPS =
  "Client to supply, per membership: full official name of the association, membership category (e.g. member/fellow), membership number, and year joined. Do not list an association without a current membership number.";

const REPLACE_YEARS_EXPERIENCE =
  "Client to supply the year the doctor first qualified to practise; years of experience is then derived from that year and never estimated. The value 0 is a deliberate non-value, not a real figure.";

// ---------------------------------------------------------------------------
// Roster
// ---------------------------------------------------------------------------

export const DOCTORS: readonly Doctor[] = [
  // -------------------------------------------------------------------------
  {
    slug: "demo-implant-specialist",
    provenance: "demo",

    name: {
      en: "[DEMO] Implant Specialist",
      ar: "[بيانات تجريبية] أخصائي زراعة الأسنان",
    },
    specialty: {
      en: "Oral implantology and implant surgery",
      ar: "زراعة الأسنان وجراحتها",
    },

    photo: demoPhoto("implant specialist", "أخصائي زراعة الأسنان"),

    bio: demoBio(
      "replacing missing teeth with dental implants and the surgical preparation that precedes it",
      "استعادة الأسنان المفقودة بالزراعة والتحضير الجراحي السابق لها",
    ),

    education: {
      value: {
        en: [
          `${EN_MARK} Placeholder — dental degree not supplied (needs: degree title, awarding university, graduation year).`,
          `${EN_MARK} Placeholder — postgraduate implantology qualification not supplied (needs: programme name, institution, year).`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لم تُقدَّم الدرجة الجامعية في طب الأسنان (المطلوب: اسم الدرجة، الجامعة المانحة، سنة التخرج).`,
          `${AR_MARK} نص بديل — لم تُقدَّم أي دراسة عليا في الزراعة (المطلوب: اسم البرنامج، الجهة، السنة).`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_EDUCATION,
    },
    certifications: {
      value: {
        en: [
          `${EN_MARK} Placeholder — no certification is claimed. Needs: certification name, issuing body, year, validity.`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لا تُنسب أي شهادة إلى هذا الملف. المطلوب: اسم الشهادة، الجهة المانحة، السنة، مدة الصلاحية.`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_CERTIFICATIONS,
    },
    memberships: {
      value: {
        en: [
          `${EN_MARK} Placeholder — no professional association is claimed. Needs: association name, membership category, membership number, year joined.`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لا تُنسب أي عضوية مهنية إلى هذا الملف. المطلوب: اسم الجمعية، نوع العضوية، رقم العضوية، سنة الانتساب.`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_MEMBERSHIPS,
    },
    yearsExperience: {
      value: 0,
      provenance: "demo",
      replaceWith: REPLACE_YEARS_EXPERIENCE,
    },

    languages: DEMO_LANGUAGES,

    clinicalInterests: {
      en: [
        "Single-tooth replacement",
        "Implant site preparation and bone grafting",
        "Full-arch rehabilitation",
      ],
      ar: [
        "تعويض السن المفقود المفرد",
        "تحضير موضع الزراعة وترقيع العظم",
        "التعويض الكامل للفك",
      ],
    },

    services: ["dental-implants", "oral-surgery"],
    cases: ["demo-single-tooth-implant"],
  },

  // -------------------------------------------------------------------------
  {
    slug: "demo-orthodontist",
    provenance: "demo",

    name: {
      en: "[DEMO] Orthodontist",
      ar: "[بيانات تجريبية] أخصائي تقويم الأسنان",
    },
    specialty: {
      en: "Orthodontics and dentofacial alignment",
      ar: "تقويم الأسنان وتصحيح الإطباق",
    },

    photo: demoPhoto("orthodontist", "أخصائي تقويم الأسنان"),

    bio: demoBio(
      "correcting crowding, spacing and bite problems with fixed braces or clear aligners",
      "تصحيح تزاحم الأسنان والفراغات ومشكلات الإطباق بالتقويم الثابت أو المشابك الشفافة",
    ),

    education: {
      value: {
        en: [
          `${EN_MARK} Placeholder — dental degree not supplied (needs: degree title, awarding university, graduation year).`,
          `${EN_MARK} Placeholder — orthodontic specialist training not supplied (needs: programme name, institution, years attended).`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لم تُقدَّم الدرجة الجامعية في طب الأسنان (المطلوب: اسم الدرجة، الجامعة المانحة، سنة التخرج).`,
          `${AR_MARK} نص بديل — لم يُقدَّم التدريب التخصصي في التقويم (المطلوب: اسم البرنامج، الجهة، سنوات الدراسة).`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_EDUCATION,
    },
    certifications: {
      value: {
        en: [
          `${EN_MARK} Placeholder — no certification is claimed. Needs: certification name, issuing body, year, validity.`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لا تُنسب أي شهادة إلى هذا الملف. المطلوب: اسم الشهادة، الجهة المانحة، السنة، مدة الصلاحية.`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_CERTIFICATIONS,
    },
    memberships: {
      value: {
        en: [
          `${EN_MARK} Placeholder — no professional association is claimed. Needs: association name, membership category, membership number, year joined.`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لا تُنسب أي عضوية مهنية إلى هذا الملف. المطلوب: اسم الجمعية، نوع العضوية، رقم العضوية، سنة الانتساب.`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_MEMBERSHIPS,
    },
    yearsExperience: {
      value: 0,
      provenance: "demo",
      replaceWith: REPLACE_YEARS_EXPERIENCE,
    },

    languages: DEMO_LANGUAGES,

    clinicalInterests: {
      en: [
        "Crowding and spacing correction",
        "Clear aligner treatment",
        "Bite and jaw-relationship problems",
        "Adolescent orthodontics",
      ],
      ar: [
        "تصحيح التزاحم والفراغات",
        "العلاج بالمشابك الشفافة",
        "مشكلات الإطباق وعلاقة الفكين",
        "تقويم أسنان المراهقين",
      ],
    },

    services: ["orthodontics"],
    cases: ["demo-clear-aligner-crowding"],
  },

  // -------------------------------------------------------------------------
  {
    slug: "demo-cosmetic-dentist",
    provenance: "demo",

    name: {
      en: "[DEMO] Cosmetic Dentist",
      ar: "[بيانات تجريبية] أخصائي تجميل الأسنان",
    },
    specialty: {
      en: "Cosmetic and restorative dentistry",
      ar: "تجميل الأسنان وطب الأسنان الترميمي",
    },

    photo: demoPhoto("cosmetic dentist", "أخصائي تجميل الأسنان"),

    bio: demoBio(
      "smile design, veneers and tooth-coloured restorations",
      "تصميم الابتسامة، والقشور الخزفية، والحشوات التجميلية بلون السن",
    ),

    education: {
      value: {
        en: [
          `${EN_MARK} Placeholder — dental degree not supplied (needs: degree title, awarding university, graduation year).`,
          `${EN_MARK} Placeholder — postgraduate aesthetic or restorative training not supplied (needs: programme name, institution, year).`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لم تُقدَّم الدرجة الجامعية في طب الأسنان (المطلوب: اسم الدرجة، الجامعة المانحة، سنة التخرج).`,
          `${AR_MARK} نص بديل — لم تُقدَّم أي دراسة عليا في التجميل أو الترميم (المطلوب: اسم البرنامج، الجهة، السنة).`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_EDUCATION,
    },
    certifications: {
      value: {
        en: [
          `${EN_MARK} Placeholder — no certification is claimed. Needs: certification name, issuing body, year, validity.`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لا تُنسب أي شهادة إلى هذا الملف. المطلوب: اسم الشهادة، الجهة المانحة، السنة، مدة الصلاحية.`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_CERTIFICATIONS,
    },
    memberships: {
      value: {
        en: [
          `${EN_MARK} Placeholder — no professional association is claimed. Needs: association name, membership category, membership number, year joined.`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لا تُنسب أي عضوية مهنية إلى هذا الملف. المطلوب: اسم الجمعية، نوع العضوية، رقم العضوية، سنة الانتساب.`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_MEMBERSHIPS,
    },
    yearsExperience: {
      value: 0,
      provenance: "demo",
      replaceWith: REPLACE_YEARS_EXPERIENCE,
    },

    languages: DEMO_LANGUAGES,

    clinicalInterests: {
      en: [
        "Smile design and shade planning",
        "Porcelain and composite veneers",
        "Tooth-coloured restorations",
        "Minimally invasive preparation",
      ],
      ar: [
        "تصميم الابتسامة واختيار درجة اللون",
        "القشور الخزفية وقشور الكمبوزيت",
        "الحشوات التجميلية بلون السن",
        "التحضير بأقل تدخل ممكن",
      ],
    },

    services: ["cosmetic-dentistry", "veneers", "hollywood-smile"],
    cases: ["demo-veneers-smile-makeover"],
  },

  // -------------------------------------------------------------------------
  {
    slug: "demo-endodontist",
    provenance: "demo",

    name: {
      en: "[DEMO] Endodontist",
      ar: "[بيانات تجريبية] أخصائي علاج جذور الأسنان",
    },
    specialty: {
      en: "Endodontics (root canal treatment)",
      ar: "علاج جذور الأسنان (سحب العصب)",
    },

    photo: demoPhoto("endodontist", "أخصائي علاج جذور الأسنان"),

    bio: demoBio(
      "diagnosing dental pain and treating infected or inflamed tooth pulp",
      "تشخيص آلام الأسنان وعلاج التهاب لبّ السن أو عدواه",
    ),

    education: {
      value: {
        en: [
          `${EN_MARK} Placeholder — dental degree not supplied (needs: degree title, awarding university, graduation year).`,
          `${EN_MARK} Placeholder — endodontic specialist training not supplied (needs: programme name, institution, years attended).`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لم تُقدَّم الدرجة الجامعية في طب الأسنان (المطلوب: اسم الدرجة، الجامعة المانحة، سنة التخرج).`,
          `${AR_MARK} نص بديل — لم يُقدَّم التدريب التخصصي في علاج الجذور (المطلوب: اسم البرنامج، الجهة، سنوات الدراسة).`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_EDUCATION,
    },
    certifications: {
      value: {
        en: [
          `${EN_MARK} Placeholder — no certification is claimed. Needs: certification name, issuing body, year, validity.`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لا تُنسب أي شهادة إلى هذا الملف. المطلوب: اسم الشهادة، الجهة المانحة، السنة، مدة الصلاحية.`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_CERTIFICATIONS,
    },
    memberships: {
      value: {
        en: [
          `${EN_MARK} Placeholder — no professional association is claimed. Needs: association name, membership category, membership number, year joined.`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لا تُنسب أي عضوية مهنية إلى هذا الملف. المطلوب: اسم الجمعية، نوع العضوية، رقم العضوية، سنة الانتساب.`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_MEMBERSHIPS,
    },
    yearsExperience: {
      value: 0,
      provenance: "demo",
      replaceWith: REPLACE_YEARS_EXPERIENCE,
    },

    languages: DEMO_LANGUAGES,

    clinicalInterests: {
      en: [
        "Diagnosis of dental pain",
        "Primary root canal treatment",
        "Retreatment of previously treated teeth",
        "Saving heavily decayed teeth",
      ],
      ar: [
        "تشخيص آلام الأسنان",
        "علاج الجذور الأولي",
        "إعادة علاج الجذور المُعالَجة سابقًا",
        "الحفاظ على الأسنان الشديدة التسوّس",
      ],
    },

    services: ["root-canal-treatment"],
    cases: ["demo-root-canal-molar"],
  },

  // -------------------------------------------------------------------------
  {
    slug: "demo-pediatric-dentist",
    provenance: "demo",

    name: {
      en: "[DEMO] Pediatric Dentist",
      ar: "[بيانات تجريبية] أخصائي طب أسنان الأطفال",
    },
    specialty: {
      en: "Pediatric dentistry",
      ar: "طب أسنان الأطفال",
    },

    photo: demoPhoto("pediatric dentist", "أخصائي طب أسنان الأطفال"),

    bio: demoBio(
      "dental care for children, from a first check-up through prevention and early treatment",
      "رعاية أسنان الأطفال، من أول زيارة فحص إلى الوقاية والعلاج المبكر",
    ),

    education: {
      value: {
        en: [
          `${EN_MARK} Placeholder — dental degree not supplied (needs: degree title, awarding university, graduation year).`,
          `${EN_MARK} Placeholder — pediatric dentistry specialist training not supplied (needs: programme name, institution, years attended).`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لم تُقدَّم الدرجة الجامعية في طب الأسنان (المطلوب: اسم الدرجة، الجامعة المانحة، سنة التخرج).`,
          `${AR_MARK} نص بديل — لم يُقدَّم التدريب التخصصي في طب أسنان الأطفال (المطلوب: اسم البرنامج، الجهة، سنوات الدراسة).`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_EDUCATION,
    },
    certifications: {
      value: {
        en: [
          `${EN_MARK} Placeholder — no certification is claimed. Needs: certification name, issuing body, year, validity.`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لا تُنسب أي شهادة إلى هذا الملف. المطلوب: اسم الشهادة، الجهة المانحة، السنة، مدة الصلاحية.`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_CERTIFICATIONS,
    },
    memberships: {
      value: {
        en: [
          `${EN_MARK} Placeholder — no professional association is claimed. Needs: association name, membership category, membership number, year joined.`,
        ],
        ar: [
          `${AR_MARK} نص بديل — لا تُنسب أي عضوية مهنية إلى هذا الملف. المطلوب: اسم الجمعية، نوع العضوية، رقم العضوية، سنة الانتساب.`,
        ],
      },
      provenance: "demo",
      replaceWith: REPLACE_MEMBERSHIPS,
    },
    yearsExperience: {
      value: 0,
      provenance: "demo",
      replaceWith: REPLACE_YEARS_EXPERIENCE,
    },

    languages: DEMO_LANGUAGES,

    clinicalInterests: {
      en: [
        "First dental visits",
        "Preventive care and fluoride",
        "Managing dental anxiety in children",
        "Early detection of bite problems",
      ],
      ar: [
        "أول زيارة لطبيب الأسنان",
        "الرعاية الوقائية والفلورايد",
        "التعامل مع خوف الأطفال من طبيب الأسنان",
        "الكشف المبكر عن مشكلات الإطباق",
      ],
    },

    services: ["pediatric-dentistry"],
    cases: ["demo-pediatric-first-visit"],
  },
] as const;

/**
 * Doctor slugs that exist as content, used to generate routes and sitemap
 * entries. Derived from DOCTORS so a route can never point at a doctor record
 * that isn't there, and a new doctor can never be added without being routed.
 */
export const DOCTOR_SLUGS: readonly string[] = DOCTORS.map((d) => d.slug);

export function getDoctor(slug: string): (typeof DOCTORS)[number] | undefined {
  return DOCTORS.find((d) => d.slug === slug);
}
